import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ImageCompressorService } from '@core/services';
import { FileRestrictions, SelectEvent } from '@progress/kendo-angular-upload';

@Component({
  selector: 'app-file-uploader',
  templateUrl: './file-uploader.component.html',
  styleUrls: ['./file-uploader.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: FileUploaderComponent,
    },
  ],
})
export class FileUploaderComponent implements ControlValueAccessor {
  @Input()
  public filesList?: Array<Partial<File>> = [];

  @Input()
  public id: string = Math.random().toString(36).substring(7);

  @Input()
  public imagesMaximumDimensions?: { height: number; width: number };

  @Input()
  public inputLabel = 'Selecciona un archivo';

  @Input()
  public multiple = true;

  @Input()
  public restrictions: FileRestrictions;

  @Input()
  public required = true;

  @Input()
  public touched = false;

  @Output()
  public extension: EventEmitter<string> = new EventEmitter();

  public base64 = '';
  public errorMsg = '';
  private onChange: (value: string) => void;
  private onTouched: () => void;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private imageCompressorService: ImageCompressorService
  ) {}

  public registerOnChange(onChange: (value: string) => void): void {
    this.onChange = (base64) => {
      this.base64 = base64;
      this.changeDetectorRef.detectChanges();
      onChange(base64);
    };
  }

  public registerOnTouched(onTouched: never): void {
    this.onTouched = onTouched;
  }

  public remove(): void {
    this.onTouched();
    this.errorMsg = '';
    this.onChange('');
  }

  public select(e: SelectEvent) {
    this.onTouched();
    this.errorMsg = '';
    if (e.files.length + (this.filesList?.length || 0) > 1) {
      this.errorMsg =
        'Es necesario remover el archivo actual para cargar uno nuevo.';
      e.preventDefault();
      return;
    }

    const file = e.files[0];

    if (file.validationErrors?.includes('invalidFileExtension')) {
      this.errorMsg = `Archivo no permitido. Solo extensiones ${this.restrictions.allowedExtensions?.join(
        ', '
      )}`;
      e.preventDefault();
      return;
    }

    if (file.validationErrors?.includes('invalidMaxFileSize')) {
      this.errorMsg = `Archivo no permitido. Máximo ${
        this.restrictions.maxFileSize! / 1000000
      } MB.`;
      e.preventDefault();
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      let result = ev.target?.result as string;
      if (result) {
        result = result.split(',')[1];
      }
      this.onChange(result);
    };

    if (file.rawFile) {
      if (this.isImage(file.extension) && this.imagesMaximumDimensions) {
        this.imageCompressorService
          .compressImage(file.rawFile, this.imagesMaximumDimensions)
          .then(
            (compressedFile) => {
              this.onChange(compressedFile.split(',')[1]);
              this.extension.emit('.webp');
            },
            () => {
              reader.readAsDataURL(file.rawFile!);
            }
          );
      } else {
        reader.readAsDataURL(file.rawFile);
      }
    }
    this.extension.emit(file.extension);
  }

  public writeValue(base64: string): void {
    this.base64 = base64;
  }

  private isImage(extension: string | undefined): boolean {
    if (!extension) {
      return false;
    }
    return ['.jpg', '.jpeg', '.png', '.webp'].includes(extension.toLowerCase());
  }
}
